import React, {useMemo, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {useDispatch, useSelector} from 'react-redux';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {DocumentBulkActionsButton} from 'app/components/general/DocumentBulkActionsButton';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {
  convertLocationParamsToFetchParams,
  useDocuments,
  useDocumentsState,
} from 'app/documents/api';
import {CompactListItem} from 'app/documents/components/CompactListItem';
import {useDocumentDashboardFilters} from 'app/documents/hooks';
import {toggleDashboardRelation} from 'app/documents/slice';
import {useFolderByHashid} from 'app/folders/api';
import {FoldersPartialDashboard} from 'app/folders/components/dashboards/FoldersPartialDashboard';
import {
  selectIsHidingStackedDashboardFolders,
  toggleIsHidingStackedDashboardFolders,
} from 'app/folders/slice';
import {ResetFilterMessage} from 'app/general/dashboards/ResetFilterMessage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {useSignoffs} from 'app/signoffs/api';
import {
  DOCUMENT_CREATE_PAGE_URL,
  DOCUMENTS_HOME,
  getDocumentsQRCodeURL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {AddDocumentToFolder} from './AddDocumentToFolder';
import {AddFolderToFolder} from './AddFolderToFolder';
import {DocumentsAddButton} from './DocumentsAddButton';
import {GoFolderUp} from './GoFolderUp';

import type {
  DashboardLocationParamsType,
  DocumentFetchParamsType,
  DocumentLocationParamsType,
} from 'app/documents/types';
import type {WithRouterProps} from 'react-router';
import type {ContentType, DocumentSerializedType} from 'sweet-types';

export function DocumentsDashboard(
  routerProps: WithRouterProps<unknown, DashboardLocationParamsType>
) {
  const folderHashid = routerProps.location.query.folder;

  const {currentData: selectedFolder} = useFolderByHashid(
    folderHashid != null ? folderHashid : skipToken
  );

  const actions = useAsyncActions({toggleDashboardRelation});
  const activeRelations = useSelector((state) => state.documents.relations);
  const {isTeamMember} = getCurrentUserPerms(useCurrentUserState().data!);

  const isHidingStackedDashboardFolders = useSelector(
    selectIsHidingStackedDashboardFolders
  );

  const dispatch = useDispatch();
  const toggleHideFolders = () =>
    dispatch(toggleIsHidingStackedDashboardFolders());

  const [addingItem, setAddingItem] = useState<'folder' | 'doc' | null>(null);

  const breadcrumbProps = useMemo(
    () => ({
      folder: selectedFolder,
      isDocsNamespace: true,
      isDocsDashboard: true,
    }),
    [selectedFolder?.hashid, folderHashid]
  );

  const {data: documents} = useDocumentsState(
    convertLocationParamsToFetchParams(routerProps.location.query)
  );

  const approvedDocumentIds = useMemo(
    () =>
      documents?.results
        .filter((document) => document.document.approved_at != null)
        .map((document) => document.object_id) ?? [],
    [documents]
  );

  const currentUser = useCurrentUserState().data;
  const {data: signoffs} = useSignoffs(
    currentUser == null || approvedDocumentIds.length == 0
      ? skipToken
      : {
          page_size: DEFAULT_PAGE_SIZE * 3,
          object_id: approvedDocumentIds,
          user_id: currentUser?.id,
          filter: 'only_unsigned',
        }
  );

  return (
    <DashboardPage<
      DocumentSerializedType,
      DocumentLocationParamsType,
      DocumentFetchParamsType
    >
      {...routerProps}
      breadcrumbProps={breadcrumbProps}
      actionArea={({isManager}) => (
        <div className="d-flex align-items-center">
          {folderHashid != null ? (
            <ActionsButton
              actions={[
                {
                  text: <a>{gt('Show QR Codes')}</a>,
                  icon: 'fa fa-qrcode',
                  key: 'qrcodes',
                  callback: () =>
                    window.open(getDocumentsQRCodeURL(folderHashid)),
                },
              ]}
            />
          ) : null}
          <DocumentsAddButton
            selectedFolderHashid={routerProps.location.query.folder}
            invisible={false}
            onAddExistingDocumentClick={() => setAddingItem('doc')}
            onNewFolderClick={() => setAddingItem('folder')}
            canAddFolder={selectedFolder?.can_edit || isManager} // user can create a document here if they can see the folder (is part of a team)
            // or if user is a super-manager (if no folder is selected)
            canCreateDocument={selectedFolder?.can_edit || isManager} // user can add a existing document if can edit the selected folder
            canAddExistingDocument={selectedFolder?.can_edit || false}
          />
        </div>
      )}
      header={() => (
        <>
          <GoFolderUp selectedFolder={selectedFolder} />

          {addingItem === 'doc' ? (
            <AddDocumentToFolder
              folderHashid={folderHashid}
              finalCallback={() => setAddingItem(null)}
            />
          ) : null}

          {addingItem === 'folder' ? (
            <AddFolderToFolder
              folderHashid={folderHashid}
              finalCallback={() => setAddingItem(null)}
              show={addingItem === 'folder'}
            />
          ) : null}

          <FoldersPartialDashboard
            selectedFolder={selectedFolder}
            {...routerProps}
          />
        </>
      )}
      sidebar={<DashboardSidebarFoldersList />}
      useDataHook={useDocuments}
      useFiltersHook={useDocumentDashboardFilters}
      contentType={'document'}
      enableNotifications={true}
      enableBulkSelect={currentUser?.is_manager ?? false}
      filterbar={({resetFilters, viewingAll}) => (
        <Filterbar
          ct="document"
          resetFilters={resetFilters}
          viewingAll={viewingAll}
          show={{
            search: true,
            sort: true,
            tags: true,
            team: true,
            filters: true,
          }}
        />
      )}
      renderItem={(
        document,
        {params, refetch},
        isSelected,
        addToSelection,
        removeFromSelection,
        notifications
      ) => (
        <CompactListItem
          key={document.id}
          object={document}
          refreshData={refetch}
          sortBy={params.ordering}
          notifications={notifications}
          activeRelations={activeRelations}
          toggleRelation={(contentType: ContentType, isOpen: boolean) =>
            actions.toggleDashboardRelation({
              contentType,
              isOpen,
            })
          }
          mySignoffs={signoffs?.results ?? []}
          currentFolder={params.folder}
          contentType={document.document_content_type}
          isSelected={isSelected}
          addToSelection={addToSelection}
          removeFromSelection={removeFromSelection}
          enableBulkSelect={currentUser?.is_manager ?? false}
          className="tour-document-item"
        />
      )}
      messages={{
        counter: (count) => ngt('%s document', '%s documents', count),
        topListActions: (selectedDocsOnThisPage, {refetch}) => (
          <div className="d-flex align-items-center">
            <button
              className="btn-sm btn btn-link text-muted"
              onClick={toggleHideFolders}
            >
              {isHidingStackedDashboardFolders
                ? gt('show folders')
                : gt('hide folders')}
            </button>
            <DocumentBulkActionsButton
              documents={selectedDocsOnThisPage}
              refreshData={refetch}
              enableBulkSelect={currentUser?.is_manager ?? false}
            />
          </div>
        ),
        notFound: ({viewingAll, resetFilters, params}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              className="js-procedures-list js-edit-dropdown"
              head={gt('No documents')}
            >
              {isTeamMember ? (
                <FullHTMLLink
                  className="btn btn-primary btn-lg"
                  html_url={
                    DOCUMENT_CREATE_PAGE_URL +
                    (params.folder != null ? `?folder=${params.folder}` : '')
                  }
                >
                  {gt('Create a Document')}
                </FullHTMLLink>
              ) : null}
            </NoResultsMessage>
          ) : (
            <NoResultsMessage
              className="js-procedures-list js-edit-dropdown"
              head={gt('No documents found with these filters')}
            >
              <ResetFilterMessage
                query={routerProps.location.query}
                ct={'document'}
                resetFilters={resetFilters}
              />
            </NoResultsMessage>
          ),
      }}
      onModalChangeMessage={({refetch}) =>
        (message, {runWhenAnyModelMatch}) => {
          runWhenAnyModelMatch(
            message,
            [
              'procedure',
              'procedureversion',
              'policy',
              'policyversion',
              'process',
              'processversion',
              'folder',
              'review',
            ],
            () => refetch()
          );
        }}
    />
  );
}

export const DocumentsDashboardBreadcrumb = (props: any) => (
  <BreadcrumbItemStructure
    {...props}
    icon="fa-home"
    text={gt('Documents')}
    active={
      props.isDocsDashboard &&
      props.link === DOCUMENTS_HOME &&
      props.folder == null
    }
    link={DOCUMENTS_HOME}
  />
);
export const TextLessDocumentsDashboardBreadcrumb = ({
  isDocsNamespace,
  ...props
}: any) =>
  isDocsNamespace ? null : (
    <BreadcrumbItemStructure {...props} icon="fa-home" link={DOCUMENTS_HOME} />
  );
